// Main copilot interfaces - centralized for better maintainability and reduced code size
import * as React from 'react';

// ============= Form Interfaces =============
export interface CascadeFieldConfig {
  parentFieldId: string;
  valueMappings: Record<string, string[]>;
  showAllWhenParentEmpty: boolean;
  clearOnParentChange: boolean;
  allOptions: string[] | Array<{key: string; text: string; description?: string}>;
}

// WI 1271833: AI-powered field validation (e.g. "Validate with AI" coaching on the
// Compete blocker description). Drives shadow-DOM injection in FormMessage.tsx.
export interface AiValidationConfig {
  enabled: boolean;
  buttonText?: string;        // default "✨ Validate with AI"
  endpoint?: string;          // external API route, default "/api/compete/validate-blocker"
  contextFields?: string[];   // other form field ids to send alongside the description
}

// WI 1278277: Async data-source config for the Opportunity ID searchable dropdown.
// Drives a debounced typeahead injected over the field's text input in FormMessage.tsx
// (same shadow-DOM enhancement pattern as AiValidationConfig).
export interface FieldDataSourceConfig {
  endpoint: string;                 // e.g. "/api/compete/opportunities"
  searchParam?: string;             // query param carrying the search text (default "search")
  minChars?: number;                // min chars before querying (default 3)
  debounceMs?: number;              // debounce window (default 300)
  valueField?: string;              // response prop used as the submitted value (default "opportunityId")
  displayField?: string;            // response prop used for the option text (default "displayName")
  fallbackToFreeText?: boolean;     // keep plain text input on error (default true)
  emptyStateMessage?: string;       // message shown when no matches
  errorStateMessage?: string;       // message shown when the source (MSX) is unavailable (HTTP 5xx)
}

export interface FormField {
  id: string;
  type: 'text' | 'textarea' | 'dropdown' | 'info' | 'peoplePicker' | 'file';
  label: string;
  placeholder?: string;
  required?: boolean;
  options?: string[];
  maxLength?: number;
  validation?: {
    pattern?: string;
    maxLength?: number;
    errorMessage?: string;
  };
  apiConfig?: {
    endpoint: string;
    queryParams?: Record<string, string>;
  };
  onChange?: {
    updateFields?: string[];
  };
  nextSection?: {
    section: string;
    showWhen: Record<string, any>;
  };
  text?: string; // for info type fields
  cascadeConfig?: CascadeFieldConfig;
  aiValidation?: AiValidationConfig;
  dataSource?: FieldDataSourceConfig; // WI 1278277: searchable dropdown source
}

export interface FormMessageProps {
  fields: FormField[];
  formTitle?: string;
  formDescription?: string;
  onFormSubmit?: (data: { values: Record<string, any>; isValid: boolean; errors: Record<string, string> }) => void;
  onShowNextSection?: (data: { sectionName: string; triggerField: string; triggerValue: any }) => void;
  apiUrl?: string;
  userEmail?: string;
  apiJsonResponse?: any; // API JSON response - will be dynamicOptions for now, later from copilot
  persistentFormValues?: Record<string, any>; // Form values to persist across submissions
  isActiveForm?: boolean; // Whether this form is the current active form for submission
}

// ============= Message Interfaces =============
export interface Message {
  id: string;
  role: 'user' | 'assistant' | 'system' | 'error' | 'separator';
  content: string;
  timestamp?: number;
  source?: string;
  intent?: string;
  // Raw payload from API for intelligent renderers (graph/table/etc.)
  payload?: any;
  // Legacy separator properties to match MCAPSBotFormContext.jsx
  showNewConversation?: boolean;
  separator?: string;
  from?: string;
  showSignInButton?: boolean;
  // Adaptive Card support
  attachments?: Array<{
    contentType: string;
    content: any;
  }>;
  meta?: {
    sources?: Array<{ title?: string; url?: string; content?: string; score?: number }>;
    followUpQuestions?: Array<{ question: string }>;
    ticketGuidance?: string;
    confidence?: number;
    processingTime?: number;
    responseType?: string;
    dataToDisplay?: any;
    // Form-related properties
    formFields?: FormField[];
    formTitle?: string;
    formDescription?: string;
    formSessionActive?: boolean;
    // File-related properties
    fileCount?: number;
    fileNames?: string[];
    // Error-related properties
    originalError?: Error | string;
    // Adaptive Card properties
    adaptiveCard?: any; // Adaptive Card JSON object
  };
}

export interface FollowUpQuestion {
  question: string;
  searchTerms?: string[];
}

// ============= Component Props Interfaces =============
export interface SimpleCopilotPanelProps {
  apiUrl: string;
  userEmail?: string;
  userFullName?: string;   // User's full name for personalization
  themeMode?: 'light' | 'dark';
  showFooterBorder?: boolean; // optional flag to show a top border above input
  /**
   * Optional initial prompt text to pre-populate the chat input.
   * Used for deep-link scenarios so that the prompt is visible but not auto-sent.
   */
  initialPrompt?: string;
  /**
   * Optional callback invoked once when the initialPrompt has been
   * auto-applied/sent. Allows callers (e.g. header) to clear any
   * stored deep-link prompt so it is not reused on later opens.
   */
  onInitialPromptApplied?: () => void;
}

export interface MessageBodyProps {
  messages: Message[];
  isLoading?: boolean;
  onFormSubmit?: (data: { values: Record<string, any>; isValid: boolean; errors: Record<string, string> }) => void;
  onShowNextSection?: (data: { sectionName: string; triggerField: string; triggerValue: any }) => void;
  userEmail?: string;
  apiUrl?: string;
  persistentFormValues?: Record<string, any>; // Store form values across submissions
  currentActiveFormId?: string | null; // Track which form is currently active for submission
  submittedFormIds?: Set<string>; // Track forms that have been submitted (permanently inactive)
  onPromptSelected?: (prompt: string) => void; 
  onExitToggle?: (checked?: boolean) => void;
  shouldDisableAllSwitches?: boolean; // Disable all exit switches based on latest API response
  switchDisableState?: {
    disableAllSwitches: boolean;
    latestMessageId: string | null;
    latestFormSessionActive: boolean;
  }; // Enhanced switch disable state for granular control
  mcapsTelemetry?: any; // Telemetry instance for logging
  onRemoveMessage?: (messageId: string) => void; // Callback to remove message
  onAdaptiveCardAction?: (actionData: any, messageId: string) => void; // Callback for Adaptive Card Action.Submit
}

export interface FooterMessageBoxProps {
  controlRef: React.RefObject<any>;
  handleSendMessage: (message: string) => void;
  placeholderText?: string;
  isLoading?: boolean;
  maxCount?: number;
  additionalActions?: React.ReactNode; // For prompt gallery button and other actions
  hasUploadedFiles?: boolean; // To track if files are uploaded for send button state
  uploadedFiles?: File[]; // Files to display as attachments
  onFileRemove?: (fileName: string) => void; // Handler to remove files by name
  onFilesChange?: (files: File[]) => void; // Handler to add new files
}

export interface FollowUpQuestionsProps {
  followUpQuestions: Array<{ question: string }>;
  handlePromptSelected: (prompt: string) => void;
  dispatch: any;
}

export interface NewConversationProps {
  /** Whether to show the new conversation button (legacy: showChangeTopic) */
  show?: boolean;
  /** Callback when new conversation is clicked (legacy: clearConversation) */
  onNewConversation?: () => void;
  /** Text to display (legacy: newConversation constant) */
  text?: string;
  /** Theme mode (legacy: theme prop) */
  theme?: 'light' | 'dark';
  /** Custom className */
  className?: string;
  /** Redux dispatch function */
  dispatch?: any;
}

// ============= Reference/Citation Interfaces =============
export interface CopilotReferenceSource {
  title?: string;
  url?: string;
  content?: string;
  score?: number;
}

export interface CopilotMessageReferenceProps {
  sources?: CopilotReferenceSource[];
  messageId: string;
  heading?: string;
  maxSources?: number;
  maxVisible?: number;
  minVisible?: number;
  className?: string;
  onSourceClick?: (source: CopilotReferenceSource) => void;
}

// ============= Message Type Interfaces =============
export interface MarkdownMessageProps {
  markdown: string;
  ticketGuidance?: string;
  sources?: CopilotReferenceSource[];
  onSourceClick?: (source: CopilotReferenceSource) => void;
}

export interface AdaptiveCardMessageProps {
  cardJson: any; // Adaptive Card JSON object
  onAction?: (actionData: any) => void; // Callback for Action.Submit events
  fallbackText?: string; // Fallback text if card rendering fails
}

export interface GraphMessageProps {
  plotData?: any[];
  layout?: any;
  structuredData?: {
    chartType: string;
    title: string;
    xAxis: {
      label: string;
      data: any[];
    };
    yAxis: {
      label: string;
      data: any[];
    };
  };
}

// ============= Context Interfaces =============
export interface FormState {
  conversationId: string;
  sessionId: string;
  count: number;
  maxCount: number;
  textareaChatValue: string;
  disableSend: boolean;
  formData?: Record<string, any>;
  isSubmitting?: boolean;
  errors?: Record<string, string>;
  isValid?: boolean;
}

export interface MCAPSCopilotFormContextType {
  formState: FormState;
  setFormState: React.Dispatch<React.SetStateAction<FormState>>;
  controlTextareaRef: React.RefObject<any>;
  clearConversation: () => void;
  handleSendMessage: (message: string) => void;
  updateFormField?: (fieldId: string, value: any) => void;
  submitForm?: () => void;
  resetForm?: () => void;
  validateForm?: () => boolean;
}

export interface MCAPSCopilotFormContextProviderProps {
  children: React.ReactNode;
  onConversationClear?: () => void;
  onSendMessage?: (message: string) => void;
  initialFormData?: Record<string, any>;
  onFormSubmit?: (formData: Record<string, any>) => void;
}

export interface MCAPSCopilotMessageContextType {
  conversations: Message[];
  setConversations: React.Dispatch<React.SetStateAction<Message[]>>;
  followUpQuestions: string[];
  updateFollowUpQuestions: (questions: string[]) => void;
  clearAllMessages: () => void;
  messages?: Message[];
  addMessage?: (message: Message) => void;
  clearMessages?: () => void;
  updateMessage?: (id: string, updates: Partial<Message>) => void;
}

export interface MCAPSCopilotMessageContextProviderProps {
  children: React.ReactNode;
  initialMessages?: Message[];
}

// ============= Panel Wrapper Interfaces =============
export interface MCAPSCopilotPanelWrapperProps {
  isOpen: boolean;
  userEmail: string;
  userFullName?: string;   // User's full name for personalization
  onClose: () => void;
  apiUrl: string;
  themeMode?: 'light' | 'dark';
  width?: string;
  position?: 'right' | 'left';
  /** Optional initial prompt text for the embedded SimpleCopilotPanel */
  initialPrompt?: string;
  /** Optional callback fired when the initial prompt is consumed */
  onInitialPromptApplied?: () => void;
}

// ============= Welcome Prompt Interfaces =============
export interface WelcomePromptProps {
  message?: string;                    // Optional custom message override
  iconSrc?: string;                    // Optional icon to display next to text
  showIcon?: boolean;                  // Control icon visibility (default true like legacy)
  className?: string;                  // Extra class for outer wrapper
  style?: React.CSSProperties;         // Inline style override
  showSuggestions?: boolean;           // Control suggestions visibility
  suggestions?: any[];   // Custom suggestions array
  onSuggestionClick?: (suggestion: string) => void; // Callback when suggestion is clicked
  suggestionsTitle?: string;           // Custom title for suggestions section
  maxSuggestions?: number;             // Maximum number of suggestions to show
  // Legacy-style props for botSuggestionMessage support
  botSuggestionMessage?: string;       // JSON string array like legacy implementation
}

// ============= Copilot Panel Size Types =============
/** Copilot panel size states - ordered from narrowest to widest */
export type CopilotPanelSize = 'default' | 'wide' | 'wider';

/** Width mapping for each panel size state */
export const COPILOT_PANEL_WIDTHS: Record<CopilotPanelSize, string> = {
  default: '26.4%',
  wide: '40%',
  wider: '55%',
};

/** Minimum viewport width (px) required to allow each panel size.
 *  Lowered 'wider' from 1280→1100 to support 110%/120% zoom on 1366px laptops. */
export const COPILOT_PANEL_MIN_VIEWPORT: Record<CopilotPanelSize, number> = {
  default: 0,      // always allowed
  wide: 1024,      // tablet landscape+
  wider: 1100,     // desktop / laptop with zoom (was 1280)
};

/** Storage key for persisted panel size */
export const COPILOT_PANEL_STORAGE_KEY = 'mcapsCopilotPanelSize';

/** Legacy storage key (for migration) */
export const COPILOT_PANEL_LEGACY_KEY = 'isCopilotFullScreen';

/** Custom event name for programmatic panel close requests */
export const COPILOT_CLOSE_EVENT = 'mcapsCopilot_requestClose';

/** Get the next panel size in the cycle */
export function getNextPanelSize(current: CopilotPanelSize): CopilotPanelSize {
  switch (current) {
    case 'default': return 'wide';
    case 'wide': return 'wider';
    case 'wider': return 'default';
  }
}

/** Clamp panel size based on viewport width */
export function clampPanelSize(desired: CopilotPanelSize, viewportWidth: number): CopilotPanelSize {
  if (desired === 'wider' && viewportWidth < COPILOT_PANEL_MIN_VIEWPORT.wider) {
    desired = 'wide';
  }
  if (desired === 'wide' && viewportWidth < COPILOT_PANEL_MIN_VIEWPORT.wide) {
    desired = 'default';
  }
  return desired;
}

// ============= Type Aliases for Common Types =============
export type ThemeMode = 'light' | 'dark';
export type MessageRole = 'user' | 'assistant' | 'system' | 'error' | 'separator';
export type FormFieldType = 'text' | 'textarea' | 'dropdown' | 'info' | 'peoplePicker' | 'file';
export type HistoryActionType = 'SET' | 'GET' | 'RESET';

// ============= Utility Type Guards =============
export const isFormMessage = (message: Message): boolean => {
  return message.meta?.responseType === 'form' && Array.isArray(message.meta?.formFields);
};

export const isGraphMessage = (message: Message): boolean => {
  return message.meta?.responseType === 'graph' && message.meta?.dataToDisplay;
};

export const isMarkdownMessage = (message: Message): boolean => {
  return !message.meta?.responseType || message.meta?.responseType === 'text' || message.meta?.responseType === 'markdown';
};

// ============= Chat Attachments Interface =============
export interface ChatAttachmentsProps {
  uploadedFiles: File[];
  onFileRemove?: (fileName: string) => void;
  maxVisibleAttachments?: number;
}

// Generated by Copilot
// updated on: 2026-01-01 | 15+ lines added | Interface Updates | Added Adaptive Card support to Message interface (attachments, meta.adaptiveCard) and created AdaptiveCardMessageProps interface | Type Definitions
